import {
  MetricsCard,
  PageHeader,
  type SelectOption,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { useMemo, useState } from "react"
import { useDataStore, useI18nStore } from "../stores/StoreContext"

type TimeRange = "7days" | "30days" | "all"

interface RepositoryStats {
  repoName: string
  score: number
  prCount: number
  scoreHistory: Array<{ label: string; value: number }>
  prHistory: Array<{ label: string; value: number }>
}

// Helper to get date range start based on time range
const getDateRangeStart = (timeRange: TimeRange): Date => {
  const now = new Date()
  if (timeRange === "7days") {
    return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "30days") {
    return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
  }
  return new Date(0) // All time
}

// Helper to determine chart granularity and period based on available data
const getChartPeriod = (
  timeRange: TimeRange,
  oldestDate: Date,
): { periods: number; unit: "day" | "week" | "month" } => {
  const now = new Date()
  const totalDays = Math.floor((now.getTime() - oldestDate.getTime()) / (24 * 60 * 60 * 1000))
  const totalWeeks = Math.floor(totalDays / 7)
  const totalMonths = Math.floor(totalDays / 30)

  if (timeRange === "7days") {
    return { periods: 7, unit: "day" }
  }

  if (timeRange === "30days") {
    // If >= 4 weeks available: last 4 weeks, else last 7 days
    if (totalWeeks >= 4) {
      return { periods: 4, unit: "week" }
    }
    return { periods: 7, unit: "day" }
  }

  // timeRange === "all"
  // If >= 3 months: last 6 months, else if >= 4 weeks: last 4 weeks, else last 7 days
  if (totalMonths >= 3) {
    return { periods: 6, unit: "month" }
  }
  if (totalWeeks >= 4) {
    return { periods: 4, unit: "week" }
  }
  return { periods: 7, unit: "day" }
}

// Helper to format date labels
const formatDateLabel = (date: Date, unit: "day" | "week" | "month"): string => {
  if (unit === "month") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })
  }
  if (unit === "week") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

// Helper to get period start date
const getPeriodStart = (
  periodIndex: number,
  totalPeriods: number,
  unit: "day" | "week" | "month",
): Date => {
  const now = new Date()
  const periodOffset = totalPeriods - periodIndex - 1

  if (unit === "day") {
    return new Date(now.getTime() - periodOffset * 24 * 60 * 60 * 1000)
  }
  if (unit === "week") {
    return new Date(now.getTime() - periodOffset * 7 * 24 * 60 * 60 * 1000)
  }
  // month
  const date = new Date(now)
  date.setMonth(date.getMonth() - periodOffset)
  return date
}

export const RepositoriesPage = observer(() => {
  const dataStore = useDataStore()
  const i18nStore = useI18nStore()
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("30days")

  // Time range options for the dropdown
  const timeRangeOptions: SelectOption[] = [
    { id: "7days", label: i18nStore.t("timeLast7Days") },
    { id: "30days", label: i18nStore.t("timeLast30Days") },
    { id: "all", label: i18nStore.t("timeAll") },
  ]

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    return getDateRangeStart(selectedTimeRange)
  }, [selectedTimeRange])

  // Get the timestamp for dependency tracking (Date objects don't work well in deps)
  const dateRangeTimestamp = dateRange.getTime()

  // Get oldest data date for chart period calculation
  const oldestDataDate = useMemo(() => {
    let oldest = new Date()

    dataStore.allCommits.forEach((commit) => {
      if (commit.timestampDate < oldest) {
        oldest = commit.timestampDate
      }
    })

    dataStore.allPullRequests.forEach((pr) => {
      if (pr.createdAtDate && pr.createdAtDate < oldest) {
        oldest = pr.createdAtDate
      }
    })

    return oldest
  }, [dataStore.allCommits, dataStore.allPullRequests])

  // Determine chart period configuration
  const chartConfig = useMemo(() => {
    return getChartPeriod(selectedTimeRange, oldestDataDate)
  }, [selectedTimeRange, oldestDataDate])

  // Calculate repository statistics with historical data
  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable arrays require snapshot conversion via .slice()
  const repositoryStats = useMemo(() => {
    // Create a snapshot of the data to avoid MobX reactivity issues
    const commits = dataStore.allCommits.slice()
    const pullRequests = dataStore.allPullRequests.slice()

    const statsMap = new Map<string, RepositoryStats>()
    const { periods, unit } = chartConfig

    // Initialize periods for all repositories
    const initializeRepository = (repoName: string): RepositoryStats => {
      const history: Array<{ label: string; value: number }> = []

      for (let i = 0; i < periods; i++) {
        const periodStart = getPeriodStart(i, periods, unit)
        history.push({
          label: formatDateLabel(periodStart, unit),
          value: 0,
        })
      }

      return {
        repoName,
        score: 0,
        prCount: 0,
        scoreHistory: history.map((h) => ({ ...h })),
        prHistory: history.map((h) => ({ ...h })),
      }
    }

    // Helper to get period index for a date
    const getPeriodIndex = (date: Date): number => {
      const now = new Date()
      let diff: number

      if (unit === "day") {
        diff = Math.floor((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))
      } else if (unit === "week") {
        diff = Math.floor((now.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000))
      } else {
        // month
        const monthsDiff =
          (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth())
        diff = monthsDiff
      }

      const periodIndex = periods - diff - 1
      return periodIndex >= 0 && periodIndex < periods ? periodIndex : -1
    }

    // Process commits for scores
    commits.forEach((commit) => {
      if (commit.timestampDate >= dateRange) {
        const current = statsMap.get(commit.repoName) || initializeRepository(commit.repoName)
        current.score += commit.score || 0

        // Add to historical data
        const periodIndex = getPeriodIndex(commit.timestampDate)
        if (periodIndex >= 0) {
          current.scoreHistory[periodIndex].value += commit.score || 0
        }

        statsMap.set(commit.repoName, current)
      }
    })

    // Process PRs for counts (merged PRs)
    pullRequests.forEach((pr) => {
      if (pr.state === "merged" && pr.mergedAtDate && pr.mergedAtDate >= dateRange) {
        const current = statsMap.get(pr.repoName) || initializeRepository(pr.repoName)
        current.prCount += 1

        // Add to historical data
        const periodIndex = getPeriodIndex(pr.mergedAtDate)
        if (periodIndex >= 0) {
          current.prHistory[periodIndex].value += 1
        }

        statsMap.set(pr.repoName, current)
      }
    })

    return Array.from(statsMap.values())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRangeTimestamp,
    chartConfig.periods,
    chartConfig.unit,
    // Force recalculation when data changes
    dataStore.allCommits.length,
    dataStore.allPullRequests.length,
  ])

  // Calculate score statistics
  const scoreStats = useMemo(() => {
    const sorted = [...repositoryStats].sort((a, b) => b.score - a.score)
    const totalScore = sorted.reduce((sum, r) => sum + r.score, 0)
    const average = sorted.length > 0 ? totalScore / sorted.length : 0

    return {
      sorted,
      average,
      total: totalScore,
    }
  }, [repositoryStats])

  // Calculate PR statistics
  const prStats = useMemo(() => {
    const sorted = [...repositoryStats].sort((a, b) => b.prCount - a.prCount)
    const totalPRs = sorted.reduce((sum, r) => sum + r.prCount, 0)
    const average = sorted.length > 0 ? totalPRs / sorted.length : 0

    return {
      sorted,
      average,
      total: totalPRs,
    }
  }, [repositoryStats])

  return (
    <div className="min-h-full bg-background">
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader
          title={i18nStore.t("repositoriesTitle")}
          subtitle={i18nStore.t("repositoriesSubtitle")}
          selectOptions={timeRangeOptions}
          selectedOptionId={selectedTimeRange}
          onSelectChange={(id) => setSelectedTimeRange(id as TimeRange)}
          selectPlaceholder={i18nStore.t("selectTimeRange")}
        >
          <Tabs defaultValue="score" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="score">{i18nStore.t("tabScore")}</TabsTrigger>
              <TabsTrigger value="prs">{i18nStore.t("tabPRs")}</TabsTrigger>
            </TabsList>

            {/* Score Tab */}
            <TabsContent value="score" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">
                  {i18nStore.t("repositoriesScoreTitle")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("repositoriesScoreDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("scoreAverage", {
                      value: scoreStats.average.toFixed(0),
                    })}
                  </span>{" "}
                  {i18nStore.t("scoreComparison")}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {scoreStats.sorted.map((repository) => {
                  const percentDiff =
                    scoreStats.average > 0
                      ? ((repository.score - scoreStats.average) / scoreStats.average) * 100
                      : 0
                  const trend: "up" | "down" | "neutral" =
                    percentDiff > 0 ? "up" : percentDiff < 0 ? "down" : "neutral"

                  return (
                    <MetricsCard
                      key={repository.repoName}
                      title={repository.repoName}
                      description={repository.score.toLocaleString()}
                      percentage={Number(percentDiff.toFixed(1))}
                      trend={trend}
                      chartData={repository.scoreHistory}
                      chartType="line"
                    />
                  )
                })}
              </div>

              {scoreStats.sorted.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("repositoriesNoData")}
                </div>
              )}
            </TabsContent>

            {/* PRs Tab */}
            <TabsContent value="prs" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">
                  {i18nStore.t("repositoriesPRsTitle")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("repositoriesPRsDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("prsAverage", {
                      value: prStats.average.toFixed(1),
                    })}
                  </span>{" "}
                  {i18nStore.t("prsComparison")}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {prStats.sorted.map((repository) => {
                  const percentDiff =
                    prStats.average > 0
                      ? ((repository.prCount - prStats.average) / prStats.average) * 100
                      : 0
                  const trend: "up" | "down" | "neutral" =
                    percentDiff > 0 ? "up" : percentDiff < 0 ? "down" : "neutral"

                  return (
                    <MetricsCard
                      key={repository.repoName}
                      title={repository.repoName}
                      description={repository.prCount.toString()}
                      percentage={Number(percentDiff.toFixed(1))}
                      trend={trend}
                      chartData={repository.prHistory}
                      chartType="line"
                    />
                  )
                })}
              </div>

              {prStats.sorted.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("repositoriesNoData")}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </PageHeader>
      </div>
    </div>
  )
})

RepositoriesPage.displayName = "RepositoriesPage"
